/**
 * nativeBridge.js - Camada de abstracao para comunicacao entre React e apps nativos (Android/iOS)
 *
 * No Android: window.AndroidInterface esta disponivel com metodos sincronos via @JavascriptInterface
 * No iOS: O WKWebView injeta window.AndroidInterface como alias para iOSInterface
 *   - Metodos de storage/identificacao retornam sincronamente (via cache local pre-populado)
 *   - getLocation e metodos de permissao retornam Promises (usar await / callNativeAsync)
 *   - Metodos fire-and-forget (save, navigation, dialogs) funcionam igual
 *
 * IMPORTANTE: O React existente pode continuar usando window.AndroidInterface diretamente.
 * Este modulo serve como documentacao e utilidade para detectar a plataforma.
 */

// Detectar plataforma
export const isAndroid = () =>
    typeof window.AndroidInterface !== 'undefined' &&
    typeof window.webkit === 'undefined';

export const isIOS = () =>
    typeof window.webkit !== 'undefined' &&
    typeof window.webkit?.messageHandlers?.iOSInterface !== 'undefined';

export const isNativeApp = () =>
    typeof window.AndroidInterface !== 'undefined';

export const getPlatform = () => {
    if (isIOS()) return 'ios';
    if (isAndroid()) return 'android';
    return 'web';
};

/**
 * Chama um metodo nativo de forma segura.
 * Funciona tanto no Android quanto no iOS porque o iOS cria window.AndroidInterface como alias.
 *
 * @param {string} method - Nome do metodo (ex: 'recoverToken', 'saveEmail')
 * @param  {...any} args - Argumentos para o metodo
 * @returns {any} Resultado do metodo nativo ou undefined se nao disponivel
 */
export function callNative(method, ...args) {
    try {
        if (window.AndroidInterface && typeof window.AndroidInterface[method] === 'function') {
            return window.AndroidInterface[method](...args);
        }
    } catch (error) {
        console.error(`[nativeBridge] Erro ao chamar ${method}:`, error);
    }
    return undefined;
}

/**
 * Chama um metodo nativo que pode retornar Promise (iOS) ou valor sincrono (Android).
 * Sempre retorna uma Promise para uniformidade.
 *
 * @param {string} method - Nome do metodo
 * @param  {...any} args - Argumentos
 * @returns {Promise<any>} Resultado do metodo nativo
 */
export async function callNativeAsync(method, ...args) {
    try {
        if (window.AndroidInterface && typeof window.AndroidInterface[method] === 'function') {
            const result = window.AndroidInterface[method](...args);
            return await result;
        }
    } catch (error) {
        console.error(`[nativeBridge] Erro ao chamar ${method}:`, error);
    }
    return undefined;
}

export default {
    isAndroid,
    isIOS,
    isNativeApp,
    getPlatform,
    callNative,
    callNativeAsync
};
